import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import StopPayoutComponent from "./component";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";

const StopPayoutContainer = ({ getDataFromAPI }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(false);

  const [formData, setFormData] = useState({
    franchiseeId: "",
    block: true
  });
  const [openDialog, setOpenDialog] = useState(false);

  // ✅ Fetch partners function with search capability
  const fetchPartners = useCallback(async (page = 0, isLoadMore = false, searchQuery = "") => {
    const isSearchingNow = !!searchQuery.trim();
    
    if ((isLoadMore && loadMoreLoading) || (!isLoadMore && loading && !isSearchingNow)) return;
    
    if (isLoadMore) {
      setLoadMoreLoading(true);
    } else if (isSearchingNow) {
      setIsSearching(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = new URLSearchParams({
        pageNumber: page,
        pageSize: pageSize
      });

      // Add search parameter if provided
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await new Promise((resolve, reject) => {
        getDataFromAPI(
          `/dashboard/api/v2/admin/franchisee?${params.toString()}`,
          "GET",
          undefined,
          resolve,
          reject
        );
      });

      if (page === 0) {
        if (isSearchingNow) {
          setFilteredPartners(response);
          setSearchMode(true);
        } else {
          setPartners(response);
          setFilteredPartners(response);
          setSearchMode(false);
        }
      } else {
        if (isSearchingNow) {
          setFilteredPartners(prev => [...prev, ...response]);
        } else {
          const newPartners = [...partners, ...response];
          setPartners(newPartners);
          setFilteredPartners(newPartners);
        }
      }

      setHasMore(response.length === pageSize);
      setPageNumber(page);
    } catch (err) {
      console.error("Partner API Error:", err);
      toast.error("Failed to load partners");
    } finally {
      if (isLoadMore) {
        setLoadMoreLoading(false);
      } else if (isSearchingNow) {
        setIsSearching(false);
      } else {
        setLoading(false);
      }
    }
  }, [loading, loadMoreLoading, getDataFromAPI, pageSize, partners]);

  // ✅ Debounced search handler for dropdown
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.trim()) {
        // Search via API
        fetchPartners(0, false, query);
      } else {
        // Clear search - show all partners
        setSearchMode(false);
        if (partners.length === 0) {
          fetchPartners(0, false);
        } else {
          setFilteredPartners(partners);
        }
      }
    }, 300),
    [fetchPartners, partners]
  );

  // ✅ Handle dropdown search change
  const handleDropdownSearchChange = (e) => {
    const query = e.target.value;
    setDropdownSearch(query);
    debouncedSearch(query);
  };

  // ✅ Clear dropdown search
  const handleClearDropdownSearch = () => {
    setDropdownSearch("");
    setSearchMode(false);
    if (partners.length === 0) {
      fetchPartners(0, false);
    } else {
      setFilteredPartners(partners);
    }
  };

  const handleOpenSelect = (status) => {
    if (status) {
      // When dropdown opens, reset dropdown search
      setDropdownSearch("");
      setSearchMode(false);
      if (partners.length === 0) {
        fetchPartners(0, false);
      } else {
        // Reset to show all partners when dropdown opens
        setFilteredPartners(partners);
      }
    }
  };

  // ✅ Load more button click handler
  const handleLoadMore = () => {
    if (!loadMoreLoading && hasMore) {
      if (searchMode) {
        // Continue search pagination
        fetchPartners(pageNumber + 1, true, dropdownSearch);
      } else {
        // Normal pagination
        fetchPartners(pageNumber + 1, true);
      }
    }
  };

  // ✅ Scroll load more
  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      !loadMoreLoading &&
      hasMore
    ) {
      handleLoadMore();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate selection
    if (!formData.franchiseeId) {
      toast.error("Please select a partner");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        franchiseeId: parseInt(formData.franchiseeId),
        block: true
      };

      await new Promise((resolve, reject) => {
        getDataFromAPI(
          "/partner/api/v3/acc/block-payout",
          "POST",
          payload,
          (response) => {
            setOpenDialog(true);
            resolve(response);
          },
          (error) => {
            toast.error(error.message || "Failed to block payout");
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while blocking payout");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    navigate(-1);
  };

  // Initial load on component mount
  useEffect(() => {
    fetchPartners(0, false);
  }, []);

  return (
    <StopPayoutComponent
      formData={formData}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      loading={loading}
      partners={filteredPartners}
      openDialog={openDialog}
      handleCloseDialog={handleCloseDialog}
      navigate={navigate}
      handleOpenSelect={handleOpenSelect}
      handleScroll={handleScroll}
      hasMore={hasMore}
      handleLoadMore={handleLoadMore}
      loadMoreLoading={loadMoreLoading}
      dropdownSearch={dropdownSearch}
      handleDropdownSearchChange={handleDropdownSearchChange}
      handleClearDropdownSearch={handleClearDropdownSearch}
      allPartners={partners}
      isSearching={isSearching}
      searchMode={searchMode}
    />
  );
};

export default connect(null, { getDataFromAPI })(StopPayoutContainer);